import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

const NavigationContext = createContext(null);

const ROUTE_LABELS = {
  dashboard: "Dashboard",
  learn: "Guides",
  "learn-more": "Guides",
  discover: "Discover",
  templates: "Templates",
  outreach: "EQ Buddy",
  approved: "Approved Contacts",
  agents: "Agents",
  history: "Mission History",
  companyenrich: "Company Enrich",
  contactenrich: "Contact Enrich",
  results: "Results",
  contacts: "Contacts",
  companies: "Companies",
  share: "Shared Results",
  legal: "Legal",
  privacy: "Privacy Policy",
  terms: "Terms of Service",
  cookies: "Cookie Policy",
  settings: "Settings",
  notifications: "Notifications",
  fuel: "Fuel",
  "fuel-credits": "Fuel & Credits",
  integrations: "Integrations",
  tools: "Tools",
  "company-enrich": "Company Enrich",
  "contact-enrich": "Contact Enrich",
  debug: "Debug",
  sitemap: "Site Map",
};

const HOME_CRUMB = {
  id: "route-home",
  label: "Home",
  path: "/",
  type: "route",
};

const normalizeLabel = (value) => {
  if (!value) return "";
  return value
    .split(/[-_/]/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const buildRouteTrail = (pathname) => {
  if (!pathname || pathname === "/") {
    return [];
  }

  const segments = pathname.split("/").filter(Boolean);
  const crumbs = [];
  let currentPath = "";

  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    const key = segment.toLowerCase();
    const label = ROUTE_LABELS[key] || normalizeLabel(segment);
    crumbs.push({
      id: `route-${index}-${segment}`,
      label,
      path: currentPath,
      type: "route",
    });
  });

  return crumbs;
};

const normalizeCrumb = (crumb, fallbackIdPrefix) => {
  if (!crumb) return null;
  const id =
    crumb.id ||
    `${fallbackIdPrefix || "crumb"}-${(crumb.label || crumb.path || "unknown")
      .toString()
      .toLowerCase()
      .replace(/\s+/g, "-")
      .slice(0, 32)}`;

  return {
    ...crumb,
    id,
    label: crumb.label || normalizeLabel(crumb.path || crumb.id),
  };
};

export function NavigationProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [routeOverride, setRouteOverride] = useState(null);
  const orderCounterRef = useRef(0);
  const [componentEntries, setComponentEntries] = useState([]);
  
  // Mobile sidebar state
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  
  const openMobileSidebar = useCallback(() => setIsMobileSidebarOpen(true), []);
  const closeMobileSidebar = useCallback(() => setIsMobileSidebarOpen(false), []);
  const toggleMobileSidebar = useCallback(() => setIsMobileSidebarOpen(prev => !prev), []);

  useEffect(() => {
    setRouteOverride(null);
    // Close mobile sidebar on route change
    setIsMobileSidebarOpen(false);
  }, [location.pathname]);

  const setRouteTrailOverride = useCallback((items) => {
    if (!items || !Array.isArray(items) || items.length === 0) {
      setRouteOverride(null);
      return;
    }
    setRouteOverride(items.map((item, index) => normalizeCrumb(item, `override-${index}`)));
  }, []);

  const clearRouteTrailOverride = useCallback(() => {
    setRouteOverride(null);
  }, []);

  const setComponentTrail = useCallback((id, segments = [], options = {}) => {
    if (!id) {
      return;
    }

    setComponentEntries((previous) => {
      const existing = previous.find((entry) => entry.id === id);

      if (!segments || segments.length === 0) {
        if (!existing) {
          return previous;
        }
        return previous.filter((entry) => entry.id !== id);
      }

      const basePriority =
        typeof options.basePriority === "number"
          ? options.basePriority
          : existing?.basePriority ??
            (orderCounterRef.current += 1) * 100;

      const normalizedSegments = segments
        .map((segment, index) =>
          normalizeCrumb(
            {
              ...segment,
              priority:
                typeof segment.priority === "number"
                  ? segment.priority
                  : basePriority + index,
              type: segment.type || "module",
            },
            `${id}-${index}`
          )
        )
        .filter(Boolean);

      const nextEntry = {
        id,
        basePriority,
        segments: normalizedSegments,
      };

      if (existing) {
        return previous.map((entry) =>
          entry.id === id ? nextEntry : entry
        );
      }

      return [...previous, nextEntry].sort(
        (a, b) => a.basePriority - b.basePriority
      );
    });
  }, []);

  const removeComponentTrail = useCallback((id) => {
    if (!id) return;
    setComponentEntries((previous) =>
      previous.some((entry) => entry.id === id)
        ? previous.filter((entry) => entry.id !== id)
        : previous
    );
  }, []);

  const moduleSegments = useMemo(() => {
    return componentEntries
      .flatMap((entry) => entry.segments || [])
      .sort((a, b) => {
        const priorityA = typeof a.priority === "number" ? a.priority : 0;
        const priorityB = typeof b.priority === "number" ? b.priority : 0;
        if (priorityA === priorityB) {
          return (a.label || "").localeCompare(b.label || "");
        }
        return priorityA - priorityB;
      });
  }, [componentEntries]);

  const routeSegments = useMemo(() => {
    if (routeOverride && Array.isArray(routeOverride)) {
      return routeOverride;
    }
    const builtTrail = buildRouteTrail(location.pathname).map((crumb, index) =>
      normalizeCrumb(crumb, `route-${index}`)
    );
    return builtTrail;
  }, [location.pathname, routeOverride]);

  const routeTrail = useMemo(() => {
    const hasHomeOverride =
      routeSegments.length > 0 &&
      routeSegments[0] &&
      routeSegments[0].path === "/";

    if (hasHomeOverride) {
      return routeSegments;
    }

    const homeCrumb = normalizeCrumb(HOME_CRUMB, "home");
    return [homeCrumb, ...routeSegments];
  }, [routeSegments]);

  const breadcrumbs = useMemo(() => {
    const combined = [...routeTrail, ...moduleSegments];
    return combined.map((crumb, index) => ({
      ...crumb,
      isActive: index === combined.length - 1,
    }));
  }, [routeTrail, moduleSegments]);

  const goBack = useCallback(() => {
    for (let i = moduleSegments.length - 1; i >= 0; i -= 1) {
      const segment = moduleSegments[i];
      if (typeof segment?.onBack === "function") {
        segment.onBack();
        return true;
      }
    }

    for (let j = routeTrail.length - 1; j >= 0; j -= 1) {
      const crumb = routeTrail[j];
      if (
        crumb.path &&
        crumb.path !== location.pathname &&
        crumb.path !== undefined
      ) {
        navigate(crumb.path);
        return true;
      }
    }

    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return true;
    }

    return false;
  }, [moduleSegments, routeTrail, navigate, location.pathname]);

  const canGoBack = useMemo(() => {
    const moduleBackAvailable = moduleSegments.some(
      (segment) => typeof segment?.onBack === "function"
    );
    if (moduleBackAvailable) {
      return true;
    }
    return routeTrail.some(
      (crumb) =>
        crumb.path &&
        crumb.path !== location.pathname &&
        crumb.path !== undefined
    );
  }, [moduleSegments, routeTrail, location.pathname]);

  const value = useMemo(
    () => ({
      breadcrumbs,
      goBack,
      canGoBack,
      setComponentTrail,
      removeComponentTrail,
      setRouteTrailOverride,
      clearRouteTrailOverride,
      // Mobile sidebar
      isMobileSidebarOpen,
      openMobileSidebar,
      closeMobileSidebar,
      toggleMobileSidebar,
    }),
    [
      breadcrumbs,
      goBack,
      canGoBack,
      setComponentTrail,
      removeComponentTrail,
      setRouteTrailOverride,
      clearRouteTrailOverride,
      isMobileSidebarOpen,
      openMobileSidebar,
      closeMobileSidebar,
      toggleMobileSidebar,
    ]
  );

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
}

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  // Return safe defaults if used outside provider (e.g., during initial render)
  if (!context) {
    return {
      breadcrumbs: [],
      goBack: () => false,
      canGoBack: false,
      setComponentTrail: () => {},
      removeComponentTrail: () => {},
      setRouteTrailOverride: () => {},
      clearRouteTrailOverride: () => {},
      isMobileSidebarOpen: false,
      openMobileSidebar: () => {},
      closeMobileSidebar: () => {},
      toggleMobileSidebar: () => {},
    };
  }
  return context;
};
